import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { fontFamilies } from "@magicbi/ui-library";
import type { PillButtonSize, PillButtonVariant } from "./PillButton.types";

/** Height (rem), horizontal padding (rem), font size (rem), gap (rem) per size. */
export const SIZE_MAP: Record<PillButtonSize, { height: string; padX: string; font: string; gap: string }> = {
  22: { height: "1.375rem", padX: "0.625rem", font: "0.6875rem", gap: "0.25rem" },
  28: { height: "1.75rem", padX: "0.75rem", font: "0.75rem", gap: "0.375rem" },
  32: { height: "2rem", padX: "0.875rem", font: "0.75rem", gap: "0.375rem" },
  40: { height: "2.5rem", padX: "1.25rem", font: "0.875rem", gap: "0.5rem" },
};

const variantStyles = (variant: PillButtonVariant) => {
  switch (variant) {
    case "primary":
      return css`
        background: ${({ theme }) => theme.colors.interactive.primary};
        color: ${({ theme }) => theme.colors.text.inverse};
        font-weight: 700;
        &:hover:not(:disabled):not([aria-disabled="true"]) {
          background: ${({ theme }) => theme.colors.interactive.primaryHover};
        }
        &:disabled,
        &[aria-disabled="true"] {
          background: ${({ theme }) => theme.colors.interactive.primaryDisabled};
          color: ${({ theme }) => theme.colors.text.inverse};
        }
      `;
    case "outline":
      return css`
        border-color: ${rawColors.stroke.strong};
        background: ${({ theme }) => theme.colors.background.primary};
        color: ${({ theme }) => theme.colors.text.primary};
        &:hover:not(:disabled):not([aria-disabled="true"]) {
          background: ${({ theme }) => theme.colors.interactive.secondaryHover};
        }
        &:disabled,
        &[aria-disabled="true"] {
          border-color: ${rawColors.stroke[200]};
          color: ${({ theme }) => theme.colors.text.tertiary};
        }
      `;
    case "ghost":
      return css`
        background: transparent;
        color: ${rawColors.brand.primary};
        &:hover:not(:disabled):not([aria-disabled="true"]) {
          background: ${({ theme }) => theme.colors.interactive.tertiaryHover};
        }
        &:disabled,
        &[aria-disabled="true"] {
          color: ${({ theme }) => theme.colors.text.tertiary};
        }
      `;
    case "ghostOnDark":
      return css`
        border-color: ${rawColors.alpha.white18};
        background: transparent;
        color: ${rawColors.stroke[200]};
        &:hover:not(:disabled):not([aria-disabled="true"]) {
          background: ${rawColors.alpha.white8};
        }
        &:disabled,
        &[aria-disabled="true"] {
          color: ${rawColors.text.subtleEmphasis};
        }
        &:focus-visible {
          outline-color: ${rawColors.stroke[200]};
        }
      `;
    default:
      return css`
        background: transparent;
        color: ${({ theme }) => theme.colors.text.link};
        &:hover:not(:disabled):not([aria-disabled="true"]) {
          color: ${({ theme }) => theme.colors.text.linkHover};
        }
        &:disabled,
        &[aria-disabled="true"] {
          color: ${({ theme }) => theme.colors.text.tertiary};
        }
      `;
  }
};

export const Button = styled.button<{ $variant: PillButtonVariant; $size: PillButtonSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  height: ${({ $size }) => SIZE_MAP[$size].height};
  padding: 0 ${({ $size, $variant }) => ($variant === "link" ? spacing[2] : SIZE_MAP[$size].padX)};
  gap: ${({ $size }) => SIZE_MAP[$size].gap};
  border: 1px solid transparent;
  border-radius: ${borderRadius.full};
  font-family: ${fontFamilies.sans};
  font-size: ${({ $size }) => SIZE_MAP[$size].font};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  ${({ $variant }) => variantStyles($variant)}

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 1px;
  }

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
  }
`;

export const Slot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;
